import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BankOption } from '../lib/tests';
import type { AnswerState } from './useSitting';
import type { HeadingSlot } from './headings';
import { DropZone } from './drag';
import { isRomanBank } from './letters';

/**
 * The Reading passage, with a heading slot above each lettered paragraph.
 *
 * The slots are inserted into the rendered passage rather than built by
 * splitting its HTML: the passage is the instructor's own markup — paragraphs,
 * emphasis, sometimes a table — and cutting it into pieces to interleave
 * controls is how that markup gets torn. A slot is an empty holder placed
 * before the paragraph, and React portals the control into it.
 */
export interface PassageSlot extends HeadingSlot {
  groupId: string;
}

export function PassagePane({
  html,
  slots,
  bank,
  answers,
  current,
  disabled,
  onChange,
  onFocusQuestion,
}: {
  html: string;
  slots: PassageSlot[];
  /** Every heading in play, so a filled slot can print what was dropped in it. */
  bank: Map<string, BankOption>;
  answers: Map<number, AnswerState>;
  current: number;
  disabled: boolean;
  onChange: (number: number, patch: Partial<AnswerState>) => void;
  onFocusQuestion: (number: number) => void;
}) {
  const roman = isRomanBank([...bank.values()]);
  const host = useRef<HTMLDivElement>(null);
  const [mounts, setMounts] = useState<{ slot: PassageSlot; el: HTMLElement }[]>([]);
  const key = slots.map((s) => `${s.number}${s.para}`).join(',');

  useEffect(() => {
    const root = host.current;
    if (!root) return;

    const made: HTMLElement[] = [];
    const next: { slot: PassageSlot; el: HTMLElement }[] = [];
    for (const slot of slots) {
      const target = root.querySelector<HTMLElement>(`[data-para="${slot.para}"]`);
      if (!target?.parentElement) continue;
      const holder = document.createElement('div');
      holder.className = 'passage__slot';
      target.parentElement.insertBefore(holder, target);
      made.push(holder);
      next.push({ slot, el: holder });
    }
    setMounts(next);

    return () => {
      for (const el of made) el.remove();
      setMounts([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, key]);

  return (
    <>
      <div className="passage" data-markable ref={host} dangerouslySetInnerHTML={{ __html: html }} />
      {mounts.map(({ slot, el }) =>
        createPortal(
          <DropZone
            groupId={slot.groupId}
            number={slot.number}
            answer={answers.get(slot.number)}
            option={bank.get((answers.get(slot.number)?.value ?? '').toUpperCase())}
            roman={roman}
            placeholder="Drop heading here"
            block
            current={slot.number === current}
            disabled={disabled}
            onChange={(patch) => onChange(slot.number, patch)}
            onFocus={() => onFocusQuestion(slot.number)}
          />,
          el,
          `slot-${slot.number}`,
        ),
      )}
    </>
  );
}
